import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import AuthProvider from './contexts/AuthProvider';
import PrivateRoute from './components/PrivateRoute';
import Login from './pages/Login';
import SSOCallback from './pages/SSOCallback';
import Inbox from './pages/Inbox';
import Endpoints from './pages/Endpoints';
import Macros from './pages/Macros';
import Categories from './pages/Categories';
import Knowledge from './pages/Knowledge';
import Backtest from './pages/Backtest';
import Review from './pages/Review';
import Architecture from './pages/Architecture';
import BlockedSenders from './pages/BlockedSenders';
import Pipeline from './pages/Pipeline';
import Settings from './pages/Settings';
import Overview from './pages/Overview';
import BibleV2 from './v2/BibleV2';
import TicketsV2 from './v2/TicketsV2';
import ControlV2 from './v2/ControlV2';
import SendV2 from './v2/SendV2';
import FeedbackV2 from './v2/FeedbackV2';
import FlowV2 from './v2/FlowV2';
import SimulateV2 from './v2/SimulateV2';
import ReviewsV2 from './v2/ReviewsV2';
import InsightsV2 from './v2/InsightsV2';
import CasesV2 from './v2/CasesV2';
import NotesV2 from './v2/NotesV2';
import CustomersV2 from './v2/CustomersV2';
import ScheduledV2 from './v2/ScheduledV2';
import ActionsV2 from './v2/ActionsV2';
import CustomsV2 from './v2/CustomsV2';
import OrdersV2 from './v2/OrdersV2';
import OrderDocsV2 from './v2/OrderDocsV2';
import DashboardV2 from './v2/DashboardV2';

// Classic Replai lives under /classic until it is retired.
const CLASSIC = [
  ['inbox', Inbox], ['categories', Categories], ['backtest', Backtest], ['review', Review],
  ['architecture', Architecture], ['blocked', BlockedSenders], ['macros', Macros], ['endpoints', Endpoints],
  ['knowledge', Knowledge], ['pipeline', Pipeline], ['settings', Settings], ['overview', Overview],
];

const guard = (el) => <PrivateRoute>{el}</PrivateRoute>;

// Links from before the move (/v2/..., classic paths, notes already posted
// in Gorgias) keep working.
const V2Redirect = () => {
  const { pathname, hash, search } = useLocation();
  return <Navigate to={`${pathname.replace(/^\/v2/, '') || '/'}${search}${hash}`} replace />;
};

function App() {
  useEffect(() => {
    // Replai is light-mode only. Strip any stale .dark class from prior sessions.
    document.documentElement.classList.remove('dark');
    localStorage.removeItem('theme');
  }, []);

  return (
    <AuthProvider>
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/sso-callback" element={<SSOCallback />} />

          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={guard(<DashboardV2 />)} />
          <Route path="/tickets" element={guard(<TicketsV2 />)} />
          <Route path="/tickets/:runId" element={guard(<TicketsV2 />)} />
          <Route path="/bible" element={guard(<BibleV2 />)} />
          <Route path="/bible/:slug" element={guard(<BibleV2 />)} />
          <Route path="/stats" element={<Navigate to="/dashboard" replace />} />
          <Route path="/control" element={guard(<ControlV2 />)} />
          <Route path="/send/:runId" element={guard(<SendV2 />)} />
          <Route path="/feedback" element={guard(<FeedbackV2 />)} />
          <Route path="/playground" element={guard(<SimulateV2 />)} />
          <Route path="/reviews" element={guard(<ReviewsV2 />)} />
          <Route path="/insights" element={guard(<InsightsV2 />)} />
          <Route path="/cases" element={guard(<CasesV2 />)} />
          <Route path="/cases/:caseId" element={guard(<CasesV2 />)} />
          <Route path="/notes" element={guard(<NotesV2 />)} />
          <Route path="/customers" element={guard(<CustomersV2 />)} />
          <Route path="/scheduled" element={guard(<ScheduledV2 />)} />
          <Route path="/flow" element={guard(<FlowV2 />)} />
          <Route path="/actions" element={guard(<ActionsV2 />)} />
          <Route path="/customs" element={guard(<CustomsV2 />)} />
          <Route path="/orders" element={guard(<OrdersV2 tab="dashboard" />)} />
          <Route path="/orders/lookup" element={guard(<OrdersV2 tab="lookup" />)} />
          <Route path="/orders/cleanup" element={guard(<OrdersV2 tab="cleanup" />)} />
          <Route path="/orders/guide" element={guard(<OrderDocsV2 />)} />

          <Route path="/v2/*" element={<V2Redirect />} />
          <Route path="/v2" element={<Navigate to="/tickets" replace />} />

          <Route path="/classic" element={<Navigate to="/classic/overview" replace />} />
          {CLASSIC.map(([path, Page]) => (
            <Route key={path} path={`/classic/${path}`} element={guard(<Page />)} />
          ))}
          {CLASSIC.map(([path]) => (
            <Route key={`old-${path}`} path={`/${path}`} element={<Navigate to={`/classic/${path}`} replace />} />
          ))}
          <Route path="*" element={<Navigate to="/tickets" replace />} />
        </Routes>
      </Router>
    </AuthProvider>
  );
}

export default App;
